import useDpi from '../hooks/useDpi';
import { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import VectorSource from 'ol/source/Vector';
import { bbox as turfBbox } from '@turf/turf';
import { MIN_SCALE, MAX_SCALE } from './constants';
import { MapContext, VectorLayer } from 'os-elements';
import { useDispatch, useSelector } from 'react-redux';
import { getGeoJsonGeometry } from 'client/utils/map/geometryUtils';
import { useMapScale } from '../hooks/useMapScale';
import * as customVectorHelpers from './customVectorHelpers';
import * as vectorFeatureLoader from './VectorFeatureLoader';
import { setAOIError } from 'client/store/mapSlice/mapSlice';

const CustomVectorSource = ({ ownerId, visible, typeName, zIndex, clippingGeometry, className, ...props }) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const { getResolutionFromScale } = useDpi();
    const { map } = useContext(MapContext);
    const scale = useMapScale();
    const declutterCustomVectors = useSelector(state => state.map?.declutterCustomVectors);
    const isVisible = Boolean(visible && ownerId);

    const minRes = getResolutionFromScale(MIN_SCALE);
    const maxRes = getResolutionFromScale(MAX_SCALE);

    const styleFunction = (feature) => {
        return customVectorHelpers.styleFunction(feature, clippingGeometry, scale);
    };

    const cacheItemLimit = 5; // Don't cache too much otherwise if you leave app open and draw loads of AOIs you'll just keep eating memory
    const cacheTimeLimit = 1000 * 60 * 5; // Don't cache after 5 minutes even if everything else is the same
    const [loaderCache, setLoaderCache] = useState([]);

    const customVectorSource = new VectorSource({
        loader: (extent, resolution, projection, success, failure) => {
            const aoiGeometry = getGeoJsonGeometry(map, 'aoi');
            if (!aoiGeometry) {
                // This happens if you load an AOI, then go to isolated views and see some annotations
                // Then you load a location bookmark.
                // eslint-disable-next-line no-console
                console.warn('CustomVectorSource: No AOI geometry found');
                return;
            }
            const bbox = turfBbox(JSON.parse(aoiGeometry));
            const projectionCode = projection.getCode();

            const drawFeatures = (features) => {
                customVectorSource.clear();
                features.forEach(feature => {
                    customVectorSource.addFeature(feature);
                });
                success(features);
            };

            const callbackSuccess = (features, typeName) => {
                drawFeatures(features);

                setLoaderCache([
                    ...loaderCache.slice(-(cacheItemLimit - 1)),
                    {
                        key,
                        features
                    }
                ]);
            };

            const callbackFailure = (err, typeName) => {
                // eslint-disable-next-line no-console
                console.error('callbackFailure: Failure ' + typeName, err);
                dispatch(setAOIError({
                    message: intl.formatMessage({ id: 'error' })
                }));
                failure(err);
            };

            const params = customVectorHelpers.getParams(bbox, projectionCode, typeName, ownerId);

            // Prevent re-requesting data for the same area within the same 5 minute window
            // This prevents network trip when zooming in and out
            // We can't use ol/loadingStrategy for this, because the bbox of the view does change when zooming
            // - we want something like strategy: aoiBBox, but that doesn't exist
            const timeKey = Math.floor(Date.now() / cacheTimeLimit) * cacheTimeLimit;
            const key = JSON.stringify(params) + timeKey;
            const cached = loaderCache.find(data => data.key === key);
            if (cached) {
                drawFeatures(cached.features);
                return;
            }

            vectorFeatureLoader.loadFeatures(params, callbackSuccess, callbackFailure);
        }
    });

    return <VectorLayer
        data-testid={props['data-testid']}
        className={className}
        id={typeName}
        declutter={declutterCustomVectors}
        source={customVectorSource}
        visible={isVisible}
        zIndex={zIndex}
        style={styleFunction}
        minResolution={minRes}
        maxResolution={maxRes}
    />;
};

CustomVectorSource.propTypes = {
    zIndex: PropTypes.number,
    ownerId: PropTypes.string,
    typeName: PropTypes.string.isRequired,
    clippingGeometry: PropTypes.object,
    visible: PropTypes.bool,
    'data-testid': PropTypes.string,
    className: PropTypes.string
};

CustomVectorSource.defaultProps = {
    zIndex: 0,
    ownerId: null,
    visible: true,
    'data-testid': 'customVectorSource',
    className: ''
};

export default CustomVectorSource;

export {
    CustomVectorSource
};
